import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";

import RehearsalRoomLogin from "@/pages/RehearsalRoom/RehearsalRoomLogin";
import AcceptInvitePage from "@/pages/RehearsalRoom/AcceptInvitePage";
import WorkspaceSelector from "@/pages/RehearsalRoom/WorkspaceSelector";
import WorkspaceHome from "@/pages/RehearsalRoom/WorkspaceHome";
import PracticeLayerRoute from "@/pages/RehearsalRoom/PracticeLayerRoute";
import ClientPracticeOverviewPage from "@/pages/RehearsalRoom/ClientPracticeOverviewPage";import RoomShell from "@/pages/RehearsalRoom/RoomShell";
import PresenceLabPage from "@/pages/RehearsalRoom/PresenceLabPage";
import OurWorkPage from "@/pages/RehearsalRoom/OurWorkPage";
import MyWorkPage from "@/pages/RehearsalRoom/MyWorkPage";
import PresentationList from "@/pages/RehearsalRoom/PresentationList";
import PresentationDetail from "@/pages/RehearsalRoom/PresentationDetail";
import WorkDocumentList from "@/pages/RehearsalRoom/WorkDocumentList";
import WorkDocumentDetail from "@/pages/RehearsalRoom/WorkDocumentDetail";
import SessionsPage from "@/pages/RehearsalRoom/SessionsPage";
import FilesPage from "@/pages/RehearsalRoom/FilesPage";
import FeedbackPage from "@/pages/RehearsalRoom/FeedbackPage";
import FeedbackDetail from "@/pages/RehearsalRoom/FeedbackDetail";
import FeedbackRequestDetail from "@/pages/RehearsalRoom/FeedbackRequestDetail";
import MessagesPage from "@/pages/RehearsalRoom/MessagesPage";
import CommercialCenterPage from "@/pages/RehearsalRoom/CommercialCenterPage";
import ReviewPage from "@/pages/RehearsalRoom/ReviewPage";
import RehearsalComparisonPage from "@/pages/RehearsalRoom/RehearsalComparisonPage";
import CoachReviewsPage from "@/pages/RehearsalRoom/CoachReviewsPage";
import CoachShell from "@/pages/RehearsalRoom/CoachShell";
import CoachTodayPage from "@/pages/RehearsalRoom/CoachTodayPage";
import CoachClientsPage from "@/pages/RehearsalRoom/CoachClientsPage";
import CoachSessionsPage from "@/pages/RehearsalRoom/coach/CoachSessionsPage";
import CoachFeedbackWorkbenchPage from "@/pages/RehearsalRoom/CoachFeedbackWorkbenchPage";
import CoachMessagesWorkbenchPage from "@/pages/RehearsalRoom/CoachMessagesWorkbenchPage";
import CoachFilesWorkbenchPage from "@/pages/RehearsalRoom/CoachFilesWorkbenchPage";
import CoachLibraryWorkbenchPage from "@/pages/RehearsalRoom/CoachLibraryWorkbenchPage";
import CoachComingSoon from "@/pages/RehearsalRoom/CoachComingSoon";
import ViewAsBanner from "@/pages/RehearsalRoom/coach/ViewAsBanner";
import OfflineBanner from "@/pages/RehearsalRoom/shared/OfflineBanner";
import Client360Page from "@/pages/RehearsalRoom/client360/Client360Page";
import Team360Page from "@/pages/RehearsalRoom/team360/Team360Page";
import RehearsalRoomComingSoon from "@/pages/RehearsalRoom/RehearsalRoomComingSoon";
import EmptyState from "@/pages/RehearsalRoom/EmptyState";
import { getTokens, rrFetch } from "@/lib/rehearsalRoomAuth";
import { useMe } from "@/lib/rehearsalRoomQueries";

/**
 * Entry resolver: the ONLY place that decides where an authenticated
 * portal user lands, per the Master v4 switcher rule —
 *   0 active memberships -> empty/support state
 *   1 active membership  -> redirect to /rehearsal-room/w/:workspaceId/home
 *   2+ active memberships -> neutral Workspace Selector
 */
function RehearsalRoomEntry() {
  const navigate = useNavigate();
  const { access } = getTokens();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["rehearsal-room", "entry", access],
    enabled: !!access,
    retry: false,
    queryFn: async () => {
      const meRes = await rrFetch("/api/auth/me");
      if (!meRes.ok) throw new Error("unauthenticated");
      const me = await meRes.json();
      if (me.portal_role === "coach_admin") return { me, home: null };
      const homeRes = await rrFetch("/api/rehearsal-room/client/home");
      if (!homeRes.ok) throw new Error("home-fetch-failed");
      const home = await homeRes.json();
      return { me, home };
    },
  });

  useEffect(() => {
    if (!data) return;
    const { me, home } = data;
    // Master v5 Prompt 16 — a coach's real home is the cross-Workspace
    // Coach Today inbox, not any single client's Workspace (the
    // single_redirect/selector switcher below stays exactly as-is, but
    // now only ever applies to clients).
    if (me.portal_role === "coach_admin") {
      navigate("/rehearsal-room/coach/today", { replace: true });
      return;
    }
    if (home.resolution === "single_redirect" && home.workspaces?.[0]) {
      navigate(`/rehearsal-room/w/${home.workspaces[0].wrk_id}/home`, { replace: true });
    }
  }, [data, navigate]);

  if (!access) return <Navigate to="/rehearsal-room/login" replace />;

  if (isLoading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper" data-testid="rehearsal-room-loading">
        <p className="text-ink-soft">Loading your Rehearsal Room…</p>
      </section>
    );
  }

  if (isError) return <Navigate to="/rehearsal-room/login" replace />;

  const { home, me } = data;
  if (me.portal_role === "coach_admin") {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper">
        <p className="text-ink-soft">Loading your Coach workspace…</p>
      </section>
    );
  }
  if (home.resolution === "empty_state") return <EmptyState role={me.portal_role} />;
  if (home.resolution === "selector") {
    return (
      <WorkspaceSelector
        workspaces={home.workspaces}
        viewerRole="client_member"
      />
    );
  }
  // single_redirect: navigation effect above is already in flight
  return (
    <section className="min-h-screen flex items-center justify-center bg-paper">
      <p className="text-ink-soft">Loading your Workspace…</p>
    </section>
  );
}

function WorkspaceHomeRoute() {
  const { workspaceId } = useParams();
  // key=workspaceId forces a full remount on switch — guarantees no stale
  // query/component state from the previous Workspace can ever be shown.
  return <WorkspaceHome key={workspaceId} />;
}

function PracticeLayerPageRoute() {
  const { workspaceId, layerKey } = useParams();
  return <PracticeLayerRoute key={`${workspaceId}-${layerKey}`} />;
}

function RoomShellRoute() {
  const { workspaceId, launchId } = useParams();
  // key forces a full remount on Workspace switch or a fresh launch —
  // guarantees no stale Room state (leases, dirty buffers, focus mode)
  // from a previous launch/Workspace can ever leak into a new one.
  return <RoomShell key={`${workspaceId}-${launchId}`} />;
}

function PresentationListRoute() {
  const { workspaceId } = useParams();
  return <PresentationList key={workspaceId} />;
}

function PresentationDetailRoute() {
  const { workspaceId, presId, areaKey } = useParams();
  return <PresentationDetail key={`${workspaceId}-${presId}-${areaKey}`} />;
}

function WorkDocumentListRoute() {
  const { workspaceId } = useParams();
  return <WorkDocumentList key={workspaceId} />;
}

function WorkDocumentDetailRoute() {
  const { workspaceId, docId } = useParams();
  return <WorkDocumentDetail key={`${workspaceId}-${docId}`} />;
}

function SessionsPageRoute() {
  const { workspaceId } = useParams();
  return <SessionsPage key={workspaceId} />;
}

function FilesPageRoute() {
  const { workspaceId } = useParams();
  return <FilesPage key={workspaceId} />;
}

function FeedbackPageRoute() {
  const { workspaceId } = useParams();
  return <FeedbackPage key={workspaceId} />;
}

function FeedbackDetailRoute() {
  const { workspaceId, fbkId } = useParams();
  return <FeedbackDetail key={`${workspaceId}-${fbkId}`} />;
}

function FeedbackRequestDetailRoute() {
  const { workspaceId, frqId } = useParams();
  return <FeedbackRequestDetail key={`${workspaceId}-${frqId}`} />;
}

function MessagesPageRoute() {
  const { workspaceId } = useParams();
  return <MessagesPage key={workspaceId} />;
}

function CommercialCenterPageRoute() {
  const { workspaceId } = useParams();
  return <CommercialCenterPage key={workspaceId} />;
}

function ReviewPageRoute() {
  const { workspaceId } = useParams();
  return <ReviewPage key={workspaceId} />;
}

function RehearsalComparisonPageRoute() {
  const { workspaceId, cmpId } = useParams();
  return <RehearsalComparisonPage key={`${workspaceId}-${cmpId}`} />;
}

function Client360PageRoute() {
  const { workspaceId, memberId, tab } = useParams();
  return <Client360Page key={`${workspaceId}-${memberId}`} workspaceId={workspaceId} memberId={memberId} tab={tab || "overview"} />;
}

function Team360PageRoute() {
  const { workspaceId, tab } = useParams();
  return <Team360Page key={workspaceId} workspaceId={workspaceId} tab={tab || "overview"} />;
}

/**
 * Master v5 Prompt 16 — every /rehearsal-room/coach/* route is
 * coach_admin-only. A client_member hitting one directly (e.g. a
 * stale bookmark) gets sent back through the entry resolver — which
 * always lands a client on their own Workspace — never the Coach
 * Shell chrome itself, even though every underlying API is already
 * independently 403'd for them.
 */
function RequireCoach({ children }) {
  const { data: me, isPending } = useMe();
  if (isPending) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper">
        <p className="text-ink-soft">Loading…</p>
      </section>
    );
  }
  if (me?.portal_role !== "coach_admin") return <Navigate to="/rehearsal-room" replace />;
  return children;
}

// Sidebar/mobile-nav destinations whose full page is a later Prompt's
// scope. `key={workspaceId}` guarantees a fresh mount on Workspace switch.
const COMING_SOON_ROUTES = [];

function ComingSoonRoute({ title, testId }) {
  const { workspaceId } = useParams();
  return <RehearsalRoomComingSoon key={workspaceId} title={title} testId={testId} />;
}

function OurWorkPageRoute() {
  const { workspaceId } = useParams();
  return <OurWorkPage key={workspaceId} />;
}

function MyWorkPageRoute() {
  const { workspaceId } = useParams();
  return <MyWorkPage key={workspaceId} />;
}

function PresenceLabPageRoute() {
  const { workspaceId, contextId } = useParams();
  return <PresenceLabPage key={`${workspaceId}-${contextId || ""}`} />;
}

// Backward-compatible alias — the canonical route is `/presence`.
function PresenceLabRedirect() {
  const { workspaceId } = useParams();
  return <Navigate to={`/rehearsal-room/w/${workspaceId}/presence`} replace />;
}

// Coach shell sidebar destinations that don't yet have their own
// cross-Workspace workbench (Prompt 20+ scope).
const COACH_COMING_SOON_ROUTES = [];

export default function RehearsalRoomApp() {
  return (
    <>
      <OfflineBanner />
      <ViewAsBanner />
      <Routes>
      <Route path="login" element={<RehearsalRoomLogin />} />
      <Route path="accept-invite" element={<AcceptInvitePage />} />
      <Route path="w/:workspaceId/home" element={<WorkspaceHomeRoute />} />
      <Route path="w/:workspaceId/room/:launchId" element={<RoomShellRoute />} />
      <Route path="w/:workspaceId/practice" element={<ClientPracticeOverviewPage />} />
      <Route path="w/:workspaceId/practice/:layerKey" element={<PracticeLayerPageRoute />} />
      <Route path="w/:workspaceId/our-work" element={<OurWorkPageRoute />} />
      <Route path="w/:workspaceId/my-work" element={<MyWorkPageRoute />} />
      <Route path="w/:workspaceId/presence" element={<PresenceLabPageRoute />} />
      <Route path="w/:workspaceId/presence/:contextId" element={<PresenceLabPageRoute />} />
      <Route path="w/:workspaceId/presence-lab" element={<PresenceLabRedirect />} />
      <Route path="w/:workspaceId/presentation" element={<PresentationListRoute />} />
      <Route path="w/:workspaceId/presentation/:presId" element={<PresentationDetailRoute />} />
      <Route path="w/:workspaceId/presentation/:presId/:areaKey" element={<PresentationDetailRoute />} />
      <Route path="w/:workspaceId/work-documents" element={<WorkDocumentListRoute />} />
      <Route path="w/:workspaceId/work-documents/:docId" element={<WorkDocumentDetailRoute />} />
      <Route path="w/:workspaceId/sessions" element={<SessionsPageRoute />} />
      <Route path="w/:workspaceId/files" element={<FilesPageRoute />} />
      <Route path="w/:workspaceId/feedback" element={<FeedbackPageRoute />} />
      <Route path="w/:workspaceId/feedback/request/:frqId" element={<FeedbackRequestDetailRoute />} />
      <Route path="w/:workspaceId/feedback/:fbkId" element={<FeedbackDetailRoute />} />
      <Route path="w/:workspaceId/messages" element={<MessagesPageRoute />} />
      <Route path="w/:workspaceId/account/commercial" element={<CommercialCenterPageRoute />} />
      <Route path="w/:workspaceId/review" element={<ReviewPageRoute />} />
      <Route path="w/:workspaceId/rehearsals/compare/:cmpId" element={<RehearsalComparisonPageRoute />} />
      <Route path="coach/today" element={<RequireCoach><CoachShell><CoachTodayPage /></CoachShell></RequireCoach>} />
      <Route path="coach/clients" element={<RequireCoach><CoachShell><CoachClientsPage /></CoachShell></RequireCoach>} />
      <Route path="coach/sessions" element={<RequireCoach><CoachShell><CoachSessionsPage /></CoachShell></RequireCoach>} />
      <Route path="coach/w/:workspaceId/client/:memberId" element={<RequireCoach><Client360PageRoute /></RequireCoach>} />
      <Route path="coach/w/:workspaceId/client/:memberId/:tab" element={<RequireCoach><Client360PageRoute /></RequireCoach>} />
      <Route path="coach/team/:workspaceId" element={<RequireCoach><Team360PageRoute /></RequireCoach>} />
      <Route path="coach/team/:workspaceId/:tab" element={<RequireCoach><Team360PageRoute /></RequireCoach>} />
      <Route path="coach/reviews" element={<RequireCoach><CoachShell><CoachReviewsPage /></CoachShell></RequireCoach>} />
      <Route path="coach/feedback" element={<RequireCoach><CoachShell><CoachFeedbackWorkbenchPage /></CoachShell></RequireCoach>} />
      <Route path="coach/messages" element={<RequireCoach><CoachShell><CoachMessagesWorkbenchPage /></CoachShell></RequireCoach>} />
      <Route path="coach/files" element={<RequireCoach><CoachShell><CoachFilesWorkbenchPage /></CoachShell></RequireCoach>} />
      <Route path="coach/library" element={<RequireCoach><CoachShell><CoachLibraryWorkbenchPage /></CoachShell></RequireCoach>} />
      {COACH_COMING_SOON_ROUTES.map((r) => (
        <Route key={r.path} path={`coach/${r.path}`} element={<RequireCoach><CoachShell><CoachComingSoon title={r.title} /></CoachShell></RequireCoach>} />
      ))}
      {COMING_SOON_ROUTES.map((r) => (
        <Route
          key={r.path}
          path={`w/:workspaceId/${r.path}`}
          element={<ComingSoonRoute title={r.title} testId={r.testId} />}
        />
      ))}
      <Route index element={<RehearsalRoomEntry />} />
      <Route path="*" element={<Navigate to="/rehearsal-room" replace />} />
      </Routes>
    </>
  );
}
